import React from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Breadcrumbs from '../marketing/components/Breadcrumbs';
import KeyTakeaways from '../marketing/components/KeyTakeaways';
import ArticleBody from '../marketing/components/ArticleBody';
import FaqAccordion from '../marketing/components/FaqAccordion';
import { getGuide, formatDate } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const pillarLabel: Record<string, string> = { compliance: 'Compliance', accounting: 'Accounting', management: 'Management' };

const GuidePage: React.FC = () => {
  useMarketingFonts();
  const { slug } = useParams<{ slug: string }>();
  const guide = getGuide(slug);
  const url = guide ? `https://www.millos.uk/guides/${guide.slug}` : '';

  useSeoHead({
    title: guide ? `${guide.title} | MillOS` : 'MillOS',
    description: guide?.description || '',
    ogType: 'article',
    canonicalPath: guide ? `/guides/${guide.slug}` : undefined,
    jsonLd: guide
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: guide.title,
            description: guide.description,
            datePublished: guide.published,
            dateModified: guide.updated,
            author: { '@type': 'Organization', name: 'MillOS' },
            publisher: { '@type': 'Organization', name: 'MillOS' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (guide.faqs || []).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.millos.uk/' },
              { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://www.millos.uk/guides' },
              { '@type': 'ListItem', position: 3, name: guide.title, item: url },
            ],
          },
        ]
      : [],
  });

  if (!guide) return <Navigate to="/guides" replace />;

  const toc = guide.sections.map((s, i) => ({ n: String(i + 1).padStart(2, '0'), heading: s.heading, anchor: `#${s.id}` }));

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <Breadcrumbs maxWidth={820} items={[{ label: 'Home', to: '/' }, { label: 'Guides', to: '/guides' }, { label: guide.title }]} />

      <Box component="article" sx={{ padding: { xs: '24px 16px 0', md: '28px 52px 0' } }}>
        <Box sx={{ maxWidth: 820, margin: '0 auto' }}>
          <Box sx={{ display: 'inline-block', background: C.tintBlue, color: C.accent, borderRadius: '32px', padding: '6px 14px', fontSize: 11, fontWeight: 500, letterSpacing: '0.06rem', textTransform: 'uppercase' }}>
            {pillarLabel[guide.pillar] || guide.pillar} · Pillar guide
          </Box>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.25rem,4.6vw,3.75rem)', lineHeight: 1.08, letterSpacing: '-0.03em', marginTop: '20px' }}>
            {guide.title}
          </Box>
          <Box sx={{ fontSize: 18, color: C.body, marginTop: '18px', lineHeight: 1.6 }}>{guide.description}</Box>
          <Box sx={{ marginTop: '20px', fontSize: 13.5, color: C.muted }}>
            {guide.readingMinutes} min read · {guide.wordCount.toLocaleString()} words · Updated {formatDate(guide.updated)}
          </Box>
        </Box>

        <Box sx={{ maxWidth: 820, margin: '44px auto 0' }}>
          <Box sx={{ background: C.ink, color: C.white, borderRadius: '20px', padding: '30px 34px', marginBottom: '44px' }}>
            <Box sx={{ fontFamily: F.display, fontSize: 24, marginBottom: '16px', color: C.white }}>In this guide</Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {toc.map((t) => (
                <Box key={t.anchor} component="a" href={t.anchor} sx={{ display: 'flex', gap: '12px', alignItems: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 15.5, '&:hover': { color: C.white } }}>
                  <Box component="span" sx={{ color: C.tocNumber }}>{t.n}</Box>
                  {t.heading}
                </Box>
              ))}
            </Box>
          </Box>

          <KeyTakeaways items={guide.keyTakeaways} />
          <ArticleBody sections={guide.sections} />

          <Box sx={{ marginTop: '56px' }}>
            <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 32, letterSpacing: '-0.02em', marginBottom: '24px' }}>
              Frequently asked questions
            </Box>
            <FaqAccordion faqs={guide.faqs} />
          </Box>
        </Box>
      </Box>

      <Box component="section" sx={{ padding: { xs: '48px 16px 0', md: '56px 52px 0' } }}>
        <Box
          sx={{
            maxWidth: 820,
            margin: '0 auto',
            background: C.heroGradient,
            borderRadius: '28px',
            padding: { xs: '40px 24px', md: '56px 40px' },
            textAlign: 'center',
            color: C.white,
          }}
        >
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(1.8rem,3.4vw,2.6rem)', letterSpacing: '-0.02em', color: C.white }}>
            Run this playbook automatically
          </Box>
          <Box
            component={RouterLink}
            to="/register"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: C.white, color: C.ink, borderRadius: '32px', padding: '13px 26px', fontSize: 16, marginTop: '24px', '&:hover': { background: C.accent, color: C.white } }}
          >
            Start free trial
          </Box>
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default GuidePage;
